import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import IconCalendar from '../components/images/IconCalendar.jsx';
import IconContact from '../components/images/IconContact.jsx';
import IconFiles from '../components/images/IconFiles.jsx';
import IconLead from '../components/images/IconLead.jsx';
import IconNotifications from '../components/images/IconNotifications.jsx';
import IconOrganize from '../components/images/IconOrganize.jsx';
import IconRocket from '../components/images/IconRocket.jsx';
import AuthLayout from '../components/layout/AuthLayout.jsx';
import BaseCard from '../components/misc/BaseCard.jsx';
import { useInfoToast } from '../hooks/useToast.jsx';
import useCurrentUser from '../hooks/useCurrentUser.js';
import {
    PERMISSION_CALENDAR,
    PERMISSION_CONTACTS,
    PERMISSION_FILES,
    PERMISSION_LEADS,
    PERMISSION_OWNED_ORGANIZATIONS,
    PERMISSION_PARTNER_ORGANIZATIONS,
    PERMISSION_PROJECTS
} from '../utils/constants.js';
import IconGlobe from '../components/images/IconGlobe.jsx';
import IconAi from '../components/images/IconAi.jsx';

const Dashboard = () => {
    const user = useSelector((state) => state.user.user);
    const navigate = useNavigate();
    const { permissions: userPermissions } = useCurrentUser();

    return (
        <AuthLayout title='Welcome' goBack={false}>
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-10'>
                <BaseCard className='my-0'>
                    <div
                        className='flex flex-row items-center cursor-pointer'
                        onClick={() => {
                            navigate('/profile');
                        }}
                    >
                        <div className='mr-4'>
                            {user && (
                                <div className='rounded-full w-20 h-20 overflow-hidden'>
                                    {user.avatar && <img src={user.avatar} alt='Avatar' />}
                                    {!user.avatar && (
                                        <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                            <div className='text-theme1-primary font-bold text-3xl -mt-1'>
                                                {user.name
                                                    .split(' ')
                                                    .map((n) => n.substring(0, 1).toUpperCase())
                                                    .join('')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            {user && user.name && (
                                <div className='text-theme1-primary font-bold text-2xl'>{user.name}</div>
                            )}
                            {user && user.job_title && (
                                <div className='text-sm text-theme1-primary2 font-bold mt-1 opacity-50'>
                                    {user.job_title}
                                </div>
                            )}
                            {user && user.company && (
                                <div className='mt-2 text-base font-bold text-theme1-text2'>{user.company}</div>
                            )}
                        </div>
                    </div>
                </BaseCard>
            </div>
            <div className='bg-theme1-tan mb-10 rounded-l-4xl'>
                <div className='max-w-3xl w-full mx-auto p-8'>
                    <div className='text-theme1-title2 font-bold text-xl mb-6'>Quick Access</div>
                    <div className='overflow-x-auto overflow-y-hidden'>
                        <div className='flex gap-[15px] items-start'>
                            <div
                                className='flex flex-col items-center justify-center cursor-pointer'
                                onClick={() => {
                                    navigate('/personal-assistant');
                                }}
                            >
                                <div className='bg-theme1-gold p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                    <IconAi className='stroke-theme1-white w-9 h-9' />
                                </div>
                                <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>Assistant</div>
                            </div>
                            <div
                                className='flex flex-col items-center justify-center cursor-pointer'
                                onClick={() => {
                                    navigate('/global-view');
                                }}
                            >
                                <div className='bg-theme1-gold p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                    <IconGlobe className='fill-theme1-white w-9 h-9' />
                                </div>
                                <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>
                                    Global View
                                </div>
                            </div>
                            {!!userPermissions[PERMISSION_LEADS]?.read && (
                                <div
                                    className='flex flex-col items-center justify-center cursor-pointer'
                                    onClick={() => {
                                        navigate('/leads');
                                    }}
                                >
                                    <div className='bg-theme1-gold p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                        <IconLead className='stroke-theme1-white' />
                                    </div>
                                    <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>Leads</div>
                                </div>
                            )}
                            {!!userPermissions[PERMISSION_CALENDAR]?.read && (
                                <div
                                    className='flex flex-col items-center justify-cente cursor-pointer'
                                    onClick={() => {
                                        navigate('/calendar');
                                    }}
                                >
                                    <div className='bg-theme1-gold p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                        <IconCalendar className='stroke-theme1-white w-9 h-9' />
                                    </div>
                                    <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>
                                        Calendar
                                    </div>
                                </div>
                            )}
                            {!!userPermissions[PERMISSION_CONTACTS]?.read && (
                                <div
                                    className='flex flex-col items-center justify-center cursor-pointer'
                                    onClick={() => {
                                        navigate('/contacts');
                                    }}
                                >
                                    <div className='bg-theme1-gold p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                        <IconContact className='stroke-theme1-white' />
                                    </div>
                                    <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>
                                        My Network
                                    </div>
                                </div>
                            )}

                            {(!!userPermissions[PERMISSION_PARTNER_ORGANIZATIONS]?.read ||
                                !!userPermissions[PERMISSION_OWNED_ORGANIZATIONS]?.read) && (
                                <div
                                    className='flex flex-col items-center justify-center cursor-pointer'
                                    onClick={() => {
                                        navigate('/organizations');
                                    }}
                                >
                                    <div className='bg-theme1-gold p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                        <IconOrganize className='stroke-theme1-white' />
                                    </div>
                                    <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>
                                        Organizations
                                    </div>
                                </div>
                            )}
                            {!!userPermissions[PERMISSION_PROJECTS]?.read && (
                                <div
                                    className='flex flex-col items-center justify-center cursor-pointer'
                                    onClick={() => {
                                        navigate('/projects');
                                    }}
                                >
                                    <div className='bg-theme1-gold p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                        <IconRocket className='stroke-theme1-white' />
                                    </div>
                                    <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>
                                        Projects
                                    </div>
                                </div>
                            )}
                            {!!userPermissions[PERMISSION_FILES]?.read && (
                                <div
                                    className='flex flex-col items-center justify-center cursor-pointer'
                                    onClick={() => {
                                        navigate('/files');
                                    }}
                                >
                                    <div className='bg-theme1-gold p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                        <IconFiles className='stroke-theme1-white' />
                                    </div>
                                    <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>Files</div>
                                </div>
                            )}

                            <div
                                className='flex flex-col items-center justify-center cursor-pointer'
                                onClick={() => {
                                    useInfoToast('Coming soon');
                                }}
                            >
                                <div className='bg-theme1-goldFrozen p-5 rounded-2xl w-20 h-20 flex items-center justify-center'>
                                    <IconNotifications className='stroke-theme1-white' />
                                </div>
                                <div className='mt-2 text-theme1-text1 font-bold text-sm whitespace-pre'>
                                    Communication Hub
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};

export default Dashboard;
